import { horizontalArea } from "../options/horizontal-area";
import { line } from "../options/line";
import { verticalArea } from "../options/vertical-area";
import { verticalLine } from "../options/vertical-line";
import { lengthDataHandler } from "./get-length";
import { groupArrays } from "./group-arrays";

type TProps = {
  data: Record<string, string>[][];
  type: string;
  colors: string[];
  primaryColor: string | undefined;
  payload:
    | {
        lineSize: string | number;
      }
    | Record<string, unknown>;
};

type SeriesData = {
  data: (number | string)[];
  type: string;
  smooth: boolean;
  lineStyle: { width: number };
  itemStyle: { color: string };
  name: string;
  // universalTransition: boolean;
  animationDurationUpdate: number;
  areaStyle?: object;
  symbolSize: number;
};

type ChartOptionGenerator = {
  [key: string]: (
    payload: Record<string, unknown>,
    data: { titles: string[]; series: SeriesData[] },
  ) => Record<string, unknown>;
};

const REVERSE_DATA = [
  "vertical-line",
  "vertical-smooth-line",
  "vertical-area",
  "vertical-smooth-area",
];

export const lineData = ({
  data,
  type,
  colors,
  primaryColor,
  payload,
}: TProps) => {
  const [seriesTitles = [], ...restData] = data;
  seriesTitles.shift();
  const grouped = groupArrays(
    restData.map((row) => row.filter((cell) => !!cell?.value)),
  );
  let [axisTitles = [], ...gropedData] = grouped;
  const titles = axisTitles.map((cell) => cell?.value ?? "");
  const shouldReverse = REVERSE_DATA.includes(type);

  if (shouldReverse) {
    axisTitles.reverse();
    gropedData = gropedData.map((arr) => arr.reverse());
  }

  const series = createSeries(
    gropedData,
    seriesTitles,
    colors,
    primaryColor,
    payload,
    type,
  );

  const options = createOptions(payload, titles, series);

  return {
    forceColumns: series.length === 1,
    option: options[type],
    ...lengthDataHandler(titles, series),
  };
};

const createSeries = (
  grouped: Record<string, string | number>[][],
  seriesTitles: Record<string, string>[],
  colors: string[],
  primaryColor: string | undefined,
  payload: Record<string, unknown>,
  type: string,
): SeriesData[] =>
  grouped.map((arr, i) => {
    const mapped = arr.map((cell) => cell?.value ?? 0);
    const width = isNaN(payload.lineSize as number)
      ? 1
      : Number(payload.lineSize);

    const seriesData: SeriesData = {
      data: mapped,
      type: "line",
      smooth: isSmoothType(type),
      lineStyle: {
        width,
      },
      symbolSize: width * 1.5,
      itemStyle: { color: colors[i] || primaryColor || "#FFFFFF" },
      name: seriesTitles[i]?.value || "",
      // universalTransition: true,
      animationDurationUpdate: 1000,
    };

    if (isAreaType(type)) {
      seriesData["areaStyle"] = {};
    }

    return seriesData;
  });

const isSmoothType = (type: string): boolean =>
  [
    "vertical-smooth-line",
    "horizontal-smooth-line",
    "vertical-smooth-area",
    "horizontal-smooth-area",
  ].includes(type);

const isAreaType = (type: string): boolean =>
  [
    "vertical-area",
    "vertical-smooth-area",
    "horizontal-area",
    "horizontal-smooth-area",
  ].includes(type);

const optionGenerators: ChartOptionGenerator = {
  line: line,
  "horizontal-smooth-line": line,
  "vertical-line": verticalLine,
  "vertical-smooth-line": verticalLine,
  "horizontal-area": horizontalArea,
  "horizontal-smooth-area": horizontalArea,
  "vertical-area": verticalArea,
  "vertical-smooth-area": verticalArea,
};

const createOptions = (
  payload: Record<string, unknown>,
  axisTitles: (string | number)[],
  series: SeriesData[],
): Record<string, unknown> =>
  Object.entries(optionGenerators).reduce(
    (options, [type, generator]) => ({
      ...options,
      [type]: generator(payload, { titles: axisTitles as string[], series }),
    }),
    {} as Record<string, unknown>,
  );
